import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CircleHelp } from "lucide-react";
import { Button, LoadingState, Modal } from "../components/ui";
import MarkdownSimples from "./MarkdownSimples";
import { interpretarMarkdown } from "./interpretarMarkdown";
import { carregarGuia, existeGuia } from "./guias";

// Largura a partir da qual o menu lateral vira barra inferior (igual ao App.js).
const LARGURA_MENU_MOBILE = 1050;

// Posição e cores ficam no próprio elemento (e não em classes do Tailwind)
// para o botão sempre aparecer por cima das telas, mesmo que o CSS ainda
// não tenha sido regenerado.
function estiloBotao(mobile) {
  return {
    position: "fixed",
    right: mobile ? "1rem" : "1.5rem",
    bottom: mobile ? "5rem" : "1.5rem",
    zIndex: 40,
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1rem",
    border: "none",
    borderRadius: "9999px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontSize: "0.875rem",
    fontWeight: 600,
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.25)",
    cursor: "pointer",
  };
}

function useTelaPequena() {
  const [pequena, setPequena] = useState(
    () => window.innerWidth < LARGURA_MENU_MOBILE,
  );

  useEffect(() => {
    const atualizar = () => setPequena(window.innerWidth < LARGURA_MENU_MOBILE);
    window.addEventListener("resize", atualizar);
    return () => window.removeEventListener("resize", atualizar);
  }, []);

  return pequena;
}

// Botão fixo "Ajuda" que abre, sobre a tela, o guia da página atual.
// O modal fica por cima: a tela e o que a pessoa já preencheu não mudam.
export default function BotaoAjuda() {
  const { pathname } = useLocation();
  const telaPequena = useTelaPequena();
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  // Ao trocar de tela, fecha o guia da tela anterior.
  useEffect(() => {
    setAberto(false);
  }, [pathname]);

  useEffect(() => {
    if (!aberto) {
      return undefined;
    }

    let cancelado = false;
    setCarregando(true);
    setErro("");

    carregarGuia(pathname)
      .then((conteudo) => {
        if (!cancelado) {
          setTexto(conteudo || "");
        }
      })
      .catch(() => {
        if (!cancelado) {
          setErro("Não foi possível abrir a ajuda agora. Tente de novo.");
        }
      })
      .finally(() => {
        if (!cancelado) {
          setCarregando(false);
        }
      });

    return () => {
      cancelado = true;
    };
  }, [aberto, pathname]);

  if (!existeGuia(pathname)) {
    return null;
  }

  const fechar = () => setAberto(false);
  const titulo = texto ? interpretarMarkdown(texto).titulo : "Ajuda";

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label="Abrir ajuda desta tela"
        title="Ajuda desta tela"
        style={estiloBotao(telaPequena)}
      >
        <CircleHelp size={20} aria-hidden="true" />
        Ajuda
      </button>

      <Modal
        open={aberto}
        onClose={fechar}
        title={titulo}
        description="Pode fechar quando quiser. Você continua de onde parou."
        className="max-w-3xl"
      >
        {carregando && <LoadingState label="Abrindo a ajuda..." />}
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        {!carregando && !erro && <MarkdownSimples texto={texto} />}

        <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
          <Button onClick={fechar}>Fechar e continuar</Button>
        </div>
      </Modal>
    </>
  );
}
